import { Fragment, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { ChevronDown, ChevronRight, ChevronUp } from "lucide-react";
import { ApiError, getAlarmHistory, getAlarms } from "../api/client.js";
import type {
  AccountKey,
  AlarmHistoryMeta,
  AlarmHistoryResponse,
  AlarmItem,
  AlarmsResponse,
  AlarmState,
} from "../api/types.js";
import { useApi } from "../lib/useApi.js";
import { formatBytes, formatNumber, formatTime } from "../lib/format.js";
import { humanizeUnitValue } from "../lib/chart-math.js";
import { MetricAxisChart } from "../components/charts.js";
import { Card, Chip, EmptyState, Pill, StatusDot } from "../components/ui.js";
import type { Tone } from "../components/ui.js";
import { EmptyState as InlineEmptyState, ErrorState, LoadingState, SectionErrors } from "../components/common.js";

// Optional detail fields the server may add later; the page degrades to "--" without them.
interface AlarmDetails extends AlarmItem {
  namespace?: string | null;
  threshold?: number | null;
  comparisonOperator?: string | null;
  thresholdUnit?: string | null;
  stateUpdatedAt?: string | null;
}

export interface AlarmsPageProps {
  refreshKey: number;
}

const COLLAPSED_ROWS = 12;
const GRID_COLUMNS = "104px minmax(0, 2.4fr) 130px minmax(0, 1.4fr) 120px 22px";
const TABLE_MIN_WIDTH = 860;

const STATE_ORDER: Record<AlarmState, number> = { ALARM: 0, INSUFFICIENT_DATA: 1, OK: 2 };
const STATE_TONE: Record<AlarmState, Tone> = { ALARM: "alarm", INSUFFICIENT_DATA: "nodata", OK: "ok" };
const STATE_LABEL: Record<AlarmState, string> = { ALARM: "ALARM", INSUFFICIENT_DATA: "NO DATA", OK: "OK" };

const ACCOUNT_COLOR: Record<AccountKey, string> = {
  prod: "var(--acct-prod)",
  staging: "var(--acct-staging)",
  shared: "var(--acct-shared)",
  management: "var(--acct-dev)",
};
const ACCOUNT_ORDER: AccountKey[] = ["prod", "staging", "shared", "management"];

const OP_SYMBOL: Record<string, string> = {
  GreaterThanThreshold: ">",
  GreaterThanOrEqualToThreshold: "≥",
  LessThanThreshold: "<",
  LessThanOrEqualToThreshold: "≤",
  GreaterThanUpperThreshold: "> band",
  LessThanLowerThreshold: "< band",
  LessThanLowerOrGreaterThanUpperThreshold: "outside band",
};

const PAGE_STYLES = `
@keyframes ops-alarm-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.35; } }
@keyframes ops-detail-in { from { opacity: 0; transform: translateY(-3px); } to { opacity: 1; transform: none; } }
.alarms-row {
  display: grid;
  width: 100%;
  align-items: center;
  padding: 10px 20px;
  border: none;
  border-bottom: 1px solid var(--divider);
  background: transparent;
  font: inherit;
  color: inherit;
  text-align: left;
  cursor: pointer;
}
.alarms-row:hover, .alarms-row--open { background: var(--bg-subtle); }
`;

function accountRank(key: AccountKey): number {
  const i = ACCOUNT_ORDER.indexOf(key);
  return i === -1 ? ACCOUNT_ORDER.length : i;
}

function inferUnit(metric: string | null): string | null {
  if (!metric) return null;
  if (/Utilization|Percent/i.test(metric)) return "Percent";
  if (/Bytes|Storage|FreeableMemory/i.test(metric)) return "Bytes";
  if (/ResponseTime|Latency|Lag/i.test(metric)) return "Seconds";
  return null;
}

function formatThresholdValue(value: number, unit: string | null): string {
  switch (unit) {
    case "Percent":
      return `${formatNumber(value)} %`;
    case "Bytes":
      return formatBytes(value);
    case "Seconds":
      return value < 1 ? `${Math.round(value * 1000)} ms` : `${formatNumber(value)} s`;
    default:
      return formatNumber(value);
  }
}

function formatThreshold(alarm: AlarmDetails): string {
  if (alarm.threshold === null || alarm.threshold === undefined) return "--";
  const value = formatThresholdValue(alarm.threshold, alarm.thresholdUnit ?? inferUnit(alarm.metric));
  const op = alarm.comparisonOperator
    ? (OP_SYMBOL[alarm.comparisonOperator] ?? alarm.comparisonOperator)
    : null;
  return op ? `${op} ${value}` : value;
}

function formatInStateFor(iso: string | null | undefined): string {
  if (!iso) return "--";
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return "--";
  const minutes = Math.max(0, Math.floor((Date.now() - then) / 60_000));
  const days = Math.floor(minutes / 1440);
  const hours = Math.floor((minutes % 1440) / 60);
  const mins = minutes % 60;
  if (days >= 7) return `${days}d`;
  if (days > 0) return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
  if (hours > 0) return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  return `${mins}m`;
}

function StatePill({ state }: { state: AlarmState }) {
  return (
    <Pill tone={STATE_TONE[state]}>
      <span
        aria-hidden="true"
        style={{
          width: 6,
          height: 6,
          borderRadius: 999,
          background: "currentColor",
          animation: state === "ALARM" ? "ops-alarm-pulse 1.6s ease-in-out infinite" : undefined,
        }}
      />
      {STATE_LABEL[state]}
    </Pill>
  );
}

function HeaderCell({ children }: { children?: string }) {
  return <span className="label--table">{children}</span>;
}

interface AlarmRowProps {
  alarm: AlarmDetails;
  open: boolean;
  onToggle: () => void;
}

function AlarmRow({ alarm, open, onToggle }: AlarmRowProps) {
  const subtitle = [alarm.namespace, alarm.metric].filter(Boolean).join(" · ");
  return (
    <button
      type="button"
      className={open ? "alarms-row alarms-row--open" : "alarms-row"}
      aria-expanded={open}
      onClick={onToggle}
      style={{ gridTemplateColumns: GRID_COLUMNS, gap: "0 12px" }}
    >
      <span>
        <StatePill state={alarm.state} />
      </span>
      <span style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 1 }}>
        <span
          className="mono"
          style={{
            fontSize: 12,
            fontWeight: 500,
            color: "var(--fg)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {alarm.name}
        </span>
        <span
          style={{
            fontSize: 11,
            color: "var(--fg-subtle)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {subtitle || "—"}
        </span>
      </span>
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          fontSize: 11.5,
          fontWeight: 600,
          color: "var(--fg-muted)",
        }}
      >
        <StatusDot square size={7} color={ACCOUNT_COLOR[alarm.account]} />
        {alarm.accountLabel}
      </span>
      <span
        className="mono"
        style={{
          fontSize: 11.5,
          color: "var(--fg-subtle)",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {formatThreshold(alarm)}
      </span>
      <span className="mono" style={{ fontSize: 11.5, color: "var(--fg-subtle)" }}>
        {formatInStateFor(alarm.stateUpdatedAt ?? alarm.updatedAt)}
      </span>
      <ChevronRight
        size={14}
        aria-hidden="true"
        style={{
          color: open ? "var(--fg-muted)" : "var(--fg-faint)",
          flex: "none",
          transform: open ? "rotate(90deg)" : undefined,
          transition: "transform var(--dur-fast) var(--ease-out)",
        }}
      />
    </button>
  );
}

interface HistoryState {
  data: AlarmHistoryResponse | null;
  loading: boolean;
  error: string | null;
  notFound: boolean;
}

function useAlarmHistory(account: AccountKey, name: string, refreshKey: number): HistoryState {
  const [state, setState] = useState<HistoryState>({ data: null, loading: true, error: null, notFound: false });
  useEffect(() => {
    let cancelled = false;
    setState({ data: null, loading: true, error: null, notFound: false });
    getAlarmHistory(account, name)
      .then((data) => {
        if (cancelled) return;
        setState({ data, loading: false, error: null, notFound: false });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 404) {
          setState({ data: null, loading: false, error: null, notFound: true });
          return;
        }
        setState({
          data: null,
          loading: false,
          error: err instanceof Error ? err.message : "unknown error",
          notFound: false,
        });
      });
    return () => {
      cancelled = true;
    };
  }, [account, name, refreshKey]);
  return state;
}

function humanizeHistoryThreshold(info: AlarmHistoryMeta, unit: string): string {
  if (info.threshold === null || info.threshold === undefined) return "--";
  const value = humanizeUnitValue(info.threshold, unit);
  const op = info.comparison ? (OP_SYMBOL[info.comparison] ?? info.comparison) : null;
  return op ? `${op} ${value}` : value;
}

function DetailShell({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        padding: "14px 20px 16px",
        borderBottom: "1px solid var(--divider)",
        background: "var(--bg-subtle)",
        animation: "ops-detail-in 0.18s var(--ease-out)",
      }}
    >
      {children}
    </div>
  );
}

function AlarmDetailPanel({ alarm, refreshKey }: { alarm: AlarmDetails; refreshKey: number }) {
  const { data, loading, error, notFound } = useAlarmHistory(alarm.account, alarm.name, refreshKey);

  if (loading) {
    return (
      <DetailShell>
        <LoadingState label="Loading metric history" />
      </DetailShell>
    );
  }
  if (notFound) {
    return (
      <DetailShell>
        <InlineEmptyState message="No metric history is available for this alarm." />
      </DetailShell>
    );
  }
  if (error) {
    return (
      <DetailShell>
        <ErrorState message={error} />
      </DetailShell>
    );
  }
  if (!data) return null;

  // CloudWatch reports unitless metrics as "None"; fall back to name-based inference.
  const unit =
    data.alarm.unit && data.alarm.unit !== "None" ? data.alarm.unit : (inferUnit(data.alarm.metric) ?? "");
  const usable = data.points.filter((p): p is { t: string; v: number } => p.v !== null);
  const values = usable.map((p) => p.v);
  const times = usable.map((p) => p.t);
  const xLabels =
    times.length >= 3 ? [formatTime(times[0]), formatTime(times[Math.floor(times.length / 2)]), "now"] : undefined;
  const metricTitle = [data.alarm.namespace, data.alarm.metric].filter(Boolean).join(" · ") || data.alarm.name;
  const thresholdText = humanizeHistoryThreshold(data.alarm, unit);

  return (
    <DetailShell>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
          <span className="mono" style={{ fontSize: 12, fontWeight: 500, color: "var(--fg)", minWidth: 0 }}>
            {metricTitle}
          </span>
          <span style={{ display: "inline-flex", alignItems: "baseline", gap: 6 }}>
            <span
              style={{
                fontSize: 10.5,
                fontWeight: 600,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "var(--fg-faint)",
              }}
            >
              alarms when
            </span>
            <span className="mono" style={{ fontSize: 11.5, color: "var(--fg-subtle)" }}>
              {thresholdText}
            </span>
          </span>
          <span style={{ marginLeft: "auto" }}>
            <StatePill state={alarm.state} />
          </span>
        </div>
        {values.length > 0 ? (
          <MetricAxisChart
            points={values}
            unit={unit}
            color={alarm.state === "ALARM" ? "var(--tone-alarm-dot)" : "var(--accent-dot)"}
            height={180}
            xLabels={xLabels}
            xLabelFor={(i) => formatTime(times[i])}
            threshold={data.alarm.threshold ?? undefined}
            thresholdLabel={
              data.alarm.threshold !== null && data.alarm.threshold !== undefined
                ? `threshold ${humanizeUnitValue(data.alarm.threshold, unit)}`
                : undefined
            }
          />
        ) : (
          <div
            style={{
              height: 180,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontFamily: "var(--font-mono)",
              fontSize: 11,
              color: "var(--fg-faint)",
              background: "var(--surface)",
              border: "1px dashed var(--border)",
              borderRadius: 10,
            }}
          >
            no datapoints in window
          </div>
        )}
        {data.errors.length > 0 && (
          <span style={{ fontSize: 11, color: "var(--tone-warn-fg)" }}>
            {data.errors.map((e) => `${e.scope}: ${e.message}`).join(" · ")}
          </span>
        )}
        <span style={{ fontSize: 11, color: "var(--fg-faint)" }}>last 3 days · 5-min resolution</span>
      </div>
    </DetailShell>
  );
}

export function AlarmsPage({ refreshKey }: AlarmsPageProps) {
  const { data, loading, error } = useApi<AlarmsResponse>(getAlarms, [], refreshKey);
  const [stateFilter, setStateFilter] = useState<"all" | AlarmState>("all");
  const [accountFilter, setAccountFilter] = useState<"all" | AccountKey>("all");
  const [expanded, setExpanded] = useState(false);
  const [openKey, setOpenKey] = useState<string | null>(null);

  const accounts = useMemo(() => {
    if (!data) return [];
    return [...data.byAccount].sort((a, b) => accountRank(a.account) - accountRank(b.account));
  }, [data]);

  const filtered = useMemo(() => {
    if (!data) return [];
    const items: AlarmDetails[] = data.alarms;
    return items
      .filter(
        (a) =>
          (stateFilter === "all" || a.state === stateFilter) &&
          (accountFilter === "all" || a.account === accountFilter),
      )
      .sort((a, b) => STATE_ORDER[a.state] - STATE_ORDER[b.state] || a.name.localeCompare(b.name));
  }, [data, stateFilter, accountFilter]);

  if (loading && !data) {
    return (
      <Card>
        <LoadingState label="Loading alarms" />
      </Card>
    );
  }
  if (error && !data) {
    return (
      <Card>
        <ErrorState message={error} />
      </Card>
    );
  }
  if (!data) {
    return (
      <Card>
        <EmptyState title="No alarm data" message="The alarms endpoint returned nothing." tone="nodata" />
      </Card>
    );
  }

  const { summary } = data;
  const truncated = !expanded && filtered.length > COLLAPSED_ROWS;
  const shown = truncated ? filtered.slice(0, COLLAPSED_ROWS) : filtered;
  const showToggle = filtered.length > COLLAPSED_ROWS;

  return (
    <>
      <style>{PAGE_STYLES}</style>

      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <Chip
          active={stateFilter === "all"}
          onClick={() => {
            setStateFilter("all");
            setExpanded(false);
          }}
          count={summary.total}
        >
          All
        </Chip>
        <Chip
          active={stateFilter === "ALARM"}
          onClick={() => setStateFilter("ALARM")}
          dot="var(--tone-alarm-dot)"
          count={summary.alarm}
        >
          In alarm
        </Chip>
        <Chip
          active={stateFilter === "INSUFFICIENT_DATA"}
          onClick={() => setStateFilter("INSUFFICIENT_DATA")}
          dot="var(--tone-nodata-dot)"
          count={summary.insufficient}
        >
          Insufficient data
        </Chip>
        <Chip
          active={stateFilter === "OK"}
          onClick={() => setStateFilter("OK")}
          dot="var(--tone-ok-dot)"
          count={summary.ok}
        >
          OK
        </Chip>
        <span className="chip-divider" />
        <Chip active={accountFilter === "all"} onClick={() => setAccountFilter("all")}>
          All accounts
        </Chip>
        {accounts.map((acc) => (
          <Chip
            key={acc.account}
            active={accountFilter === acc.account}
            onClick={() => setAccountFilter(acc.account)}
            dot={ACCOUNT_COLOR[acc.account]}
          >
            {acc.accountLabel}
          </Chip>
        ))}
      </div>

      <Card flush>
        <div style={{ overflowX: "auto" }}>
          <div style={{ minWidth: TABLE_MIN_WIDTH }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: GRID_COLUMNS,
                gap: "0 12px",
                padding: "9px 20px",
                borderBottom: "1px solid var(--divider)",
                background: "var(--bg-subtle)",
              }}
            >
              <HeaderCell>State</HeaderCell>
              <HeaderCell>Alarm</HeaderCell>
              <HeaderCell>Account</HeaderCell>
              <HeaderCell>Threshold</HeaderCell>
              <HeaderCell>In state for</HeaderCell>
              <HeaderCell />
            </div>
            {shown.length === 0 ? (
              <EmptyState
                title={data.alarms.length === 0 ? "No alarms configured" : "No matching alarms"}
                message={
                  data.alarms.length === 0
                    ? "No CloudWatch alarms were found across the monitored accounts."
                    : "No alarms match the current state and account filters."
                }
                tone="nodata"
              />
            ) : (
              shown.map((alarm) => {
                const key = `${alarm.account}-${alarm.name}`;
                const open = openKey === key;
                return (
                  <Fragment key={key}>
                    <AlarmRow
                      alarm={alarm}
                      open={open}
                      onToggle={() => setOpenKey(open ? null : key)}
                    />
                    {open && <AlarmDetailPanel alarm={alarm} refreshKey={refreshKey} />}
                  </Fragment>
                );
              })
            )}
          </div>
        </div>
        {showToggle && (
          <button
            type="button"
            onClick={() => setExpanded((e) => !e)}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 7,
              width: "100%",
              padding: 11,
              border: "none",
              background: "transparent",
              color: "var(--accent)",
              fontFamily: "var(--font-body)",
              fontSize: 12.5,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            {truncated ? `Show all ${filtered.length} alarms` : "Collapse list"}
            {truncated ? (
              <ChevronDown size={14} aria-hidden="true" />
            ) : (
              <ChevronUp size={14} aria-hidden="true" />
            )}
          </button>
        )}
      </Card>

      <SectionErrors errors={data.errors} />
    </>
  );
}
